import duoModelUrl from "@/assets/duo-star-white.glb?url";
import { transformUrl, useAuth } from "@/auth/auth";
import { useOrganization } from "@/client/backend/organization.tsx";
import { useUser } from "@/client/backend/user.tsx";
import { orgRequestOptions } from "@/client/director/org.ts";
import {
  fetchJsonlCapture,
  useNetworkLogCapture,
  useSessionArtifacts,
  type SessionArtifact,
  type SessionEvent,
  type SessionLogLine,
  type SessionPlatform,
} from "@/client/director/sessionArtifacts.tsx";
import { AppShell, PageHeader } from "@/components/layout/AppShell";
import {
  LiveStreamBox,
  useMicrophoneErrorToast,
} from "@/components/session/LiveStreamBox.tsx";
import { NetworkRequestInspector } from "@/components/session/NetworkRequestInspector.tsx";
import {
  EventRow,
  LiveCapturePanels,
  LiveNetworkPanel,
  tunnelApiUrlFor,
} from "@/components/session/SessionCapturePanels.tsx";
import { SessionColumns } from "@/components/session/SessionColumns.tsx";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatSize } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Limrun } from "@limrun/api";
import { RemoteControl } from "@limrun/ui";
import { Download, Network } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import useSWR from "swr";

// InstanceView is the slice of an iOS or Android instance this page reads;
// both platforms' status carries the same fields.
type InstanceView = {
	status: {
		state: string;
		apiUrl?: string;
		endpointWebSocketUrl?: string;
		adbWebSocketUrl?: string;
		token?: string;
	};
};

// Poll while the instance is still coming up so the live panels appear
// without a manual refresh, mirroring the builds page.
const instanceRefreshInterval = (latest: InstanceView | undefined) =>
	latest && (latest.status.state === "creating" || latest.status.state === "assigned")
		? 3_000
		: 0;

// useInstance fetches the instance via the director SDK on the route-declared
// platform. Errors keep the page rendering the persisted-artifact sections,
// which only need the instance id.
function useInstance(
	platform: SessionPlatform,
	instanceId: string | undefined,
	organizationTid: string | undefined,
): { instance: InstanceView | "loading" | undefined; error?: Error } {
	const auth = useAuth();
	const { data, error } = useSWR(
		instanceId ? ["sessionsInstance", platform, instanceId, organizationTid] : null,
		async (): Promise<InstanceView> => {
			const client: Limrun = auth.regionClient;
			const options = orgRequestOptions(organizationTid);
			return platform === "ios"
				? client.iosInstances.get(instanceId as string, options)
				: client.androidInstances.get(instanceId as string, options);
		},
		{
			refreshInterval: instanceRefreshInterval,
		},
	);
	return {
		instance: instanceId ? data ?? "loading" : undefined,
		error: error instanceof Error ? error : undefined,
	};
}

// useCaptureEntries downloads and parses a persisted JSONL capture once.
// Keyed by artifact id because presigned URLs rotate while persisted content
// is immutable.
function useCaptureEntries<T extends { ts: number }>(
	artifact: SessionArtifact | undefined,
): { entries: T[] | "loading"; error?: Error } {
	const { data, error } = useSWR(
		artifact ? ["sessionCapture", artifact.id] : null,
		async () => fetchJsonlCapture<T>((artifact as SessionArtifact).downloadUrl),
	);
	return {
		entries: data ?? "loading",
		error: error instanceof Error ? error : undefined,
	};
}

// activeEntryIndex is the index of the last entry whose ts is at or before
// the playback cutoff (-1 before the first one): entries are sorted by ts, so
// a binary search runs on every timeupdate without scanning the whole capture.
function activeEntryIndex(entries: { ts: number }[], cutoffMs: number): number {
	let lo = 0;
	let hi = entries.length;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if (entries[mid].ts <= cutoffMs) lo = mid + 1;
		else hi = mid;
	}
	return lo - 1;
}

// formatOffset renders a playback offset as m:ss (or h:mm:ss past an hour).
function formatOffset(ms: number): string {
	const totalSecs = Math.max(0, Math.floor(ms / 1000));
	const secs = totalSecs % 60;
	const mins = Math.floor(totalSecs / 60) % 60;
	const hours = Math.floor(totalSecs / 3600);
	const mmss = `${mins}:${secs.toString().padStart(2, "0")}`;
	return hours > 0 ? `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}` : mmss;
}

function formatCaptureDuration(artifact: SessionArtifact): string | undefined {
	if (!artifact.startedAt || !artifact.stoppedAt) return undefined;
	const ms = Date.parse(artifact.stoppedAt) - Date.parse(artifact.startedAt);
	if (!Number.isFinite(ms) || ms < 0) return undefined;
	return formatOffset(ms);
}

// DownloadButton opens the artifact's presigned URL in a new tab; presigned
// GETs carry their own auth, so a plain anchor is all it takes.
function DownloadButton({ artifact }: { artifact: SessionArtifact }) {
	return (
		<TooltipProvider>
			<Tooltip delayDuration={0}>
				<TooltipTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7"
						asChild
						aria-label="Download artifact"
					>
						<a href={artifact.downloadUrl} target="_blank" rel="noreferrer" download>
							<Download className="h-3.5 w-3.5" />
						</a>
					</Button>
				</TooltipTrigger>
				<TooltipContent>Download</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}

const artifactKindLabels: Record<SessionArtifact["kind"], string> = {
	recording: "Recording",
	appLog: "App logs",
	eventLog: "Events",
	networkLog: "Network log",
};

// CaptureMetaRow lists one persisted app-log or event capture with its
// metadata and download action.
function CaptureMetaRow({ artifact }: { artifact: SessionArtifact }) {
	const duration = formatCaptureDuration(artifact);
	return (
		<div className="flex items-center gap-3 border-t px-3 py-2 text-xs first:border-t-0">
			<Badge variant="outline" className="shrink-0 px-2 py-0.5 text-xs font-medium">
				{artifactKindLabels[artifact.kind]}
			</Badge>
			{artifact.bundleId && (
				<span className="truncate font-mono text-muted-foreground" title={artifact.bundleId}>
					{artifact.bundleId}
				</span>
			)}
			{artifact.startedAt && (
				<span className="shrink-0 text-muted-foreground">
					{new Date(artifact.startedAt).toLocaleString()}
				</span>
			)}
			{duration && <span className="shrink-0 text-muted-foreground">{duration}</span>}
			{artifact.sizeBytes != null && (
				<span className="shrink-0 text-muted-foreground">{formatSize(artifact.sizeBytes)}</span>
			)}
			{artifact.expiresAt && (
				<span className="shrink-0 text-muted-foreground">
					expires {new Date(artifact.expiresAt).toLocaleDateString()}
				</span>
			)}
			<div className="ml-auto shrink-0">
				<DownloadButton artifact={artifact} />
			</div>
		</div>
	);
}

// NetworkCaptureBody renders one persisted HAR inside the playback layout's
// network column: capture metadata on top, the request inspector below.
function NetworkCaptureBody({ artifact }: { artifact: SessionArtifact }) {
	const { data, error } = useNetworkLogCapture(artifact, true);
	const duration = formatCaptureDuration(artifact);
	return (
		<div className="flex min-h-[12rem] flex-1 flex-col gap-2">
			<div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
				{artifact.startedAt && (
					<span>{new Date(artifact.startedAt).toLocaleString()}</span>
				)}
				{duration && <span>{duration}</span>}
				{artifact.sizeBytes != null && <span>{formatSize(artifact.sizeBytes)}</span>}
				{artifact.tunnelId && (
					<span className="font-mono">{artifact.tunnelId}</span>
				)}
				{artifact.selectors?.map((selector) => (
					<Badge
						key={selector}
						variant="outline"
						className="font-mono text-[10px]"
					>
						{selector}
					</Badge>
				))}
				<div className="ml-auto">
					<DownloadButton artifact={artifact} />
				</div>
			</div>
			{error ? (
				<div className="space-y-1">
					<p className="text-sm text-red-600">
						Failed to load network capture: {error.message}
					</p>
					<p className="text-xs text-muted-foreground">
						The original HAR is still available from the download button.
					</p>
				</div>
			) : data === "loading" ? (
				<p className="text-sm text-muted-foreground">
					Loading network capture...
				</p>
			) : (
				<NetworkRequestInspector
					requests={data.requests}
					warnings={data.warnings}
					variant="compact"
					emptyMessage="This HAR contains no requests."
				/>
			)}
		</div>
	);
}

// overlapsRecording reports whether a capture's time range intersects the
// recording's. A capture without startedAt cannot be aligned to playback, so
// it never syncs; a missing stoppedAt (capture cut off at SIGTERM) is treated
// as open-ended.
function overlapsRecording(capture: SessionArtifact, recStartMs: number, recStopMs: number): boolean {
	if (!capture.startedAt) return false;
	const captureStart = Date.parse(capture.startedAt);
	if (!Number.isFinite(captureStart)) return false;
	const captureStop = capture.stoppedAt ? Date.parse(capture.stoppedAt) : Number.POSITIVE_INFINITY;
	return captureStart <= recStopMs && captureStop >= recStartMs;
}

// SyncedLogPanel shows a persisted app-log capture in full and highlights the
// line playback is currently at, keeping it scrolled into view; clicking a
// line pauses the recording and seeks it to that moment.
function SyncedLogPanel({
	artifact,
	recStartMs,
	cutoffMs,
	onSeek,
}: {
	artifact: SessionArtifact;
	recStartMs: number;
	cutoffMs: number;
	onSeek: (offsetMs: number) => void;
}) {
	const { entries, error } = useCaptureEntries<SessionLogLine>(artifact);
	const all = entries === "loading" ? [] : entries;
	const activeIndex = activeEntryIndex(all, cutoffMs);
	const activeRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		activeRef.current?.scrollIntoView({ block: "nearest" });
	}, [activeIndex]);
	if (error) {
		return <p className="p-3 text-sm text-red-600">Failed to load app logs: {error.message}</p>;
	}
	if (entries === "loading") {
		return <p className="p-3 text-sm text-muted-foreground">Loading...</p>;
	}
	return (
		<ScrollArea className="min-h-0 flex-1">
			{all.length === 0 ? (
				<p className="p-3 text-xs text-muted-foreground">
					This capture has no log lines.
				</p>
			) : (
				<div className="flex flex-col p-2 font-mono text-xs">
					{all.map((entry, i) => (
						<button
							key={`${entry.ts}-${i}`}
							ref={i === activeIndex ? activeRef : undefined}
							type="button"
							onClick={() => onSeek(entry.ts - recStartMs)}
							className={cn(
								"flex items-baseline gap-2 rounded border-l-2 px-1 py-0.5 text-left transition-colors",
								i === activeIndex
									? "border-l-blue-600 bg-slate-200/80 hover:bg-slate-200"
									: "border-l-transparent hover:bg-muted/50",
							)}
						>
							<span className="shrink-0 tabular-nums text-muted-foreground">
								{formatOffset(entry.ts - recStartMs)}
							</span>
							<span className="whitespace-pre-wrap break-all">{entry.line}</span>
						</button>
					))}
				</div>
			)}
		</ScrollArea>
	);
}

// SyncedEventPanel is the event-capture counterpart of SyncedLogPanel.
function SyncedEventPanel({
	artifact,
	recStartMs,
	cutoffMs,
	onSeek,
}: {
	artifact: SessionArtifact;
	recStartMs: number;
	cutoffMs: number;
	onSeek: (offsetMs: number) => void;
}) {
	const { entries, error } = useCaptureEntries<SessionEvent>(artifact);
	const all = entries === "loading" ? [] : entries;
	const activeIndex = activeEntryIndex(all, cutoffMs);
	const activeRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		activeRef.current?.scrollIntoView({ block: "nearest" });
	}, [activeIndex]);
	if (error) {
		return <p className="p-3 text-sm text-red-600">Failed to load events: {error.message}</p>;
	}
	if (entries === "loading") {
		return <p className="p-3 text-sm text-muted-foreground">Loading...</p>;
	}
	return (
		<ScrollArea className="min-h-0 flex-1">
			{all.length === 0 ? (
				<p className="p-3 text-xs text-muted-foreground">
					This capture has no events.
				</p>
			) : (
				<div className="flex flex-col gap-0.5 p-2">
					{all.map((event, i) => (
						<EventRow
							key={`${event.ts}-${i}`}
							event={event}
							timeLabel={formatOffset(event.ts - recStartMs)}
							onClick={() => onSeek(event.ts - recStartMs)}
							active={i === activeIndex}
							rowRef={i === activeIndex ? activeRef : undefined}
						/>
					))}
				</div>
			)}
		</ScrollArea>
	);
}

// PlaybackSection mirrors the live view's three columns for a terminated
// session: events and app logs on the left, the recording in the middle,
// network captures on the right, with the same draggable dividers. Every
// column always renders; a kind that was not persisted says so in place, so
// the layout never changes based on what was captured. Capture entries are
// shown in full; the one playback is currently at is highlighted, and
// clicking an entry seeks the video to it.
function PlaybackSection({
	recording,
	appLog,
	events,
	networkCaptures,
}: {
	recording?: SessionArtifact;
	appLog?: SessionArtifact;
	events?: SessionArtifact;
	networkCaptures: SessionArtifact[];
}) {
	const videoRef = useRef<HTMLVideoElement>(null);
	// timeupdate fires a few times a second, which is plenty for tracking the
	// highlighted entry; seeking snaps it immediately.
	const [currentTimeMs, setCurrentTimeMs] = useState(0);

	const recStartMs = recording?.startedAt
		? Date.parse(recording.startedAt)
		: Number.NaN;
	const canSeek = Number.isFinite(recStartMs);
	// Without a recording nothing is ever "current", so no entry highlights.
	const cutoffMs = canSeek ? recStartMs + currentTimeMs : Number.NEGATIVE_INFINITY;
	// Offset labels anchor to the recording when playback can sync to it,
	// otherwise to the capture's own start.
	const anchorFor = (artifact: SessionArtifact) =>
		canSeek
			? recStartMs
			: artifact.startedAt
				? Date.parse(artifact.startedAt)
				: 0;

	// Clicking an entry pauses playback at that moment so the user can study
	// the frame and resume with the video controls when ready.
	const seekTo = (offsetMs: number) => {
		const video = videoRef.current;
		if (!canSeek || !video) return;
		video.pause();
		video.currentTime = Math.max(0, offsetMs / 1000);
		setCurrentTimeMs(Math.max(0, offsetMs));
	};

	const metaBits = recording
		? [
				recording.startedAt
					? new Date(recording.startedAt).toLocaleString()
					: undefined,
				formatCaptureDuration(recording),
				recording.sizeBytes != null ? formatSize(recording.sizeBytes) : undefined,
				recording.expiresAt
					? `expires ${new Date(recording.expiresAt).toLocaleDateString()}`
					: undefined,
			].filter(Boolean)
		: [];

	const captureColumn = (className: string) => (
		<div className={cn("flex min-h-0 flex-col gap-3", className)}>
			<div className="flex min-h-[8rem] flex-1 flex-col overflow-hidden rounded-lg border bg-background">
				<div className="flex items-center border-b bg-muted/50 px-2 py-1">
					<span className="text-xs font-semibold">Events</span>
					{events && (
						<div className="ml-auto">
							<DownloadButton artifact={events} />
						</div>
					)}
				</div>
				{events ? (
					<SyncedEventPanel
						artifact={events}
						recStartMs={anchorFor(events)}
						cutoffMs={cutoffMs}
						onSeek={seekTo}
					/>
				) : (
					<p className="p-3 text-sm text-muted-foreground">
						No events were persisted for this session.
					</p>
				)}
			</div>
			<div className="flex min-h-[8rem] flex-1 flex-col overflow-hidden rounded-lg border bg-background">
				<div className="flex items-center gap-2 border-b bg-muted/50 px-2 py-1">
					<span className="text-xs font-semibold">App logs</span>
					{appLog?.bundleId && (
						<span className="truncate font-mono text-[10px] text-muted-foreground">
							{appLog.bundleId}
						</span>
					)}
					{appLog && (
						<div className="ml-auto">
							<DownloadButton artifact={appLog} />
						</div>
					)}
				</div>
				{appLog ? (
					<SyncedLogPanel
						artifact={appLog}
						recStartMs={anchorFor(appLog)}
						cutoffMs={cutoffMs}
						onSeek={seekTo}
					/>
				) : (
					<p className="p-3 text-sm text-muted-foreground">
						No app logs were persisted for this session.
					</p>
				)}
			</div>
		</div>
	);

	const videoColumn = (
		<div className="flex h-full min-h-[24rem] flex-col overflow-hidden rounded-lg border bg-background">
			<div className="flex items-center gap-3 border-b px-3 py-2 text-xs">
				<h3 className="text-sm font-semibold">Recording</h3>
				{metaBits.length > 0 && (
					<span className="truncate text-muted-foreground">
						{metaBits.join(" · ")}
					</span>
				)}
				{recording && (
					<div className="ml-auto shrink-0">
						<DownloadButton artifact={recording} />
					</div>
				)}
			</div>
			<div className="flex min-h-0 flex-1 items-center justify-center bg-muted/90 p-2">
				{recording ? (
					<video
						ref={videoRef}
						controls
						preload="metadata"
						src={recording.downloadUrl}
						className="max-h-full max-w-full rounded-md"
						onTimeUpdate={(e) =>
							setCurrentTimeMs(e.currentTarget.currentTime * 1000)
						}
					/>
				) : (
					<p className="p-4 text-center text-sm text-muted-foreground">
						No recording was persisted for this session.
					</p>
				)}
			</div>
		</div>
	);

	const networkColumn = (className: string) => (
		<div
			className={cn(
				"flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background",
				className,
			)}
		>
			<div className="flex items-center gap-2 border-b px-3 py-2">
				<Network className="h-4 w-4 text-muted-foreground" />
				<h3 className="text-sm font-semibold">Network requests</h3>
			</div>
			{networkCaptures.length === 0 ? (
				<p className="p-3 text-sm text-muted-foreground">
					No network capture was persisted for this session. Tunnels started
					with --persist keep one.
				</p>
			) : (
				<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-3">
					{networkCaptures.map((artifact) => (
						<NetworkCaptureBody key={artifact.id} artifact={artifact} />
					))}
				</div>
			)}
		</div>
	);

	return (
		<SessionColumns
			left={captureColumn}
			center={videoColumn}
			right={networkColumn}
		/>
	);
}

export function SessionsPage() {
	const { platform: platformParam, instanceId } = useParams<{
		platform: string;
		instanceId: string;
	}>();
	const platform = platformParam === "ios" || platformParam === "android" ? platformParam : undefined;
	const { user } = useUser();
	const [organization] = useOrganization();
	const organizationTid = organization !== "loading" ? organization.tid : undefined;

	const { instance } = useInstance(platform ?? "android", platform ? instanceId : undefined, organizationTid);
	const instanceReady = instance !== "loading" && instance?.status.state === "ready";
	const instanceStarting =
		instance !== "loading" &&
		(instance?.status.state === "creating" || instance?.status.state === "assigned");
	const apiUrl = instanceReady ? instance.status.apiUrl : undefined;
	const instanceToken = instanceReady ? instance.status.token : undefined;
	const streamUrl = instanceReady ? instance.status.endpointWebSocketUrl : undefined;
	const tunnelApiUrl = instanceReady
		? tunnelApiUrlFor(platform ?? "android", instance.status)
		: undefined;
	const microphoneErrorToast = useMicrophoneErrorToast();
	// Unique per-tab: the runtime tears down existing peer connections when a
	// new offer reuses an id, so sharing the instance id would collapse
	// multi-viewer streams (same trick as the playground).
	const streamSessionId = useMemo(() => {
		const randomSuffix =
			Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
		return `${instanceId ?? "session"}-${randomSuffix}`;
	}, [instanceId]);

	// Persisted artifacts (all kinds in one request), polled while the
	// instance runs so captures appear shortly after they stop.
	const refreshIntervalMs = instanceReady ? 10_000 : undefined;
	const { data: artifacts, error: artifactsError } = useSessionArtifacts(
		platform ?? "android",
		platform ? instanceId : undefined,
		{ organizationTid, enabled: !!platform, refreshIntervalMs },
	);

	if (!platform || !instanceId) {
		return <Navigate to="/instances" replace />;
	}

	// Hold rendering until the user record resolves so the org switcher
	// below always has a concrete organization list.
	if (user === "loading") {
		return (
			<AppShell title="Session"><p role="status" className="text-muted-foreground">Loading session...</p></AppShell>
		);
	}

	const artifactList = artifacts === "loading" ? [] : artifacts;
	const recordingList = artifactList.filter((a) => a.kind === "recording");
	const appLogCaptures = artifactList.filter((a) => a.kind === "appLog");
	const eventCaptures = artifactList.filter((a) => a.kind === "eventLog");
	const networkLogCaptures = artifactList
		.filter((a) => a.kind === "networkLog")
		.sort((a, b) => {
			const aMs = a.startedAt ? Date.parse(a.startedAt) : 0;
			const bMs = b.startedAt ? Date.parse(b.startedAt) : 0;
			return aMs - bMs;
		});

	// Pair each recording with the captures that overlap it in time; those
	// render inside its playback section. When nothing was recorded, one
	// section still renders with the first capture of each kind, so the page
	// always has the same three columns as the playground. Only captures no
	// section shows get a standalone metadata row below.
	const playbackSections = recordingList.map((recording) => {
		const recStartMs = recording.startedAt ? Date.parse(recording.startedAt) : Number.NaN;
		const recStopMs = recording.stoppedAt ? Date.parse(recording.stoppedAt) : Number.POSITIVE_INFINITY;
		const canSync = Number.isFinite(recStartMs);
		return {
			key: recording.id,
			recording: recording as SessionArtifact | undefined,
			appLog: canSync
				? appLogCaptures.find((c) => overlapsRecording(c, recStartMs, recStopMs))
				: undefined,
			events: canSync
				? eventCaptures.find((c) => overlapsRecording(c, recStartMs, recStopMs))
				: undefined,
		};
	});
	if (playbackSections.length === 0) {
		playbackSections.push({
			key: "no-recording",
			recording: undefined,
			appLog: appLogCaptures[0],
			events: eventCaptures[0],
		});
	}
	const shownIds = new Set(
		playbackSections.flatMap((s) =>
			[s.appLog?.id, s.events?.id].filter((id): id is string => !!id),
		),
	);
	const leftoverCaptures = [...appLogCaptures, ...eventCaptures]
		.filter((c) => !shownIds.has(c.id))
		.sort((a, b) => {
			const aMs = a.startedAt ? Date.parse(a.startedAt) : 0;
			const bMs = b.startedAt ? Date.parse(b.startedAt) : 0;
			return aMs - bMs;
		});
	const artifactsLoading = artifacts === "loading" && !artifactsError;
	const nothingPersisted =
		!artifactsLoading &&
		recordingList.length === 0 &&
		appLogCaptures.length === 0 &&
		eventCaptures.length === 0 &&
		networkLogCaptures.length === 0;
	// While the instance runs or starts, the live view is the page; playback
	// sections join it only once something was persisted. After termination
	// the playback layout always renders, its columns saying what is missing.
	const showPlayback =
		!artifactsLoading &&
		!instanceStarting &&
		(!instanceReady || !nothingPersisted);

	return (
		<AppShell title="Session">
			<PageHeader title="Session" description={<span className="font-mono text-xs">{instanceId}</span>} />
			{instanceStarting && (
				<p className="text-sm text-muted-foreground">
					Instance is starting up, check back soon.
				</p>
			)}

			{/* Live view while the instance runs: capture panels on the
					    left, the live stream in the middle, network requests on
					    the right, with draggable dividers between the columns
					    on wide screens and a plain stack below that. */}
			{instanceReady &&
				apiUrl &&
				(() => {
					const capturePanels = (className: string) => (
						<LiveCapturePanels
							apiUrl={apiUrl}
							token={instanceToken}
							signalingUrl={
								streamUrl ? transformUrl(streamUrl) ?? streamUrl : undefined
							}
							platform={platform}
							className={className}
						/>
					);
					const streamView = (
						<LiveStreamBox className="min-h-[24rem]">
							{streamUrl && instanceToken ? (
								<RemoteControl
									duoModelUrl={duoModelUrl}
									url={transformUrl(streamUrl) ?? streamUrl}
									token={instanceToken}
									showFrame={false}
									autoReconnect
									sessionId={streamSessionId}
									onMicrophoneStateChange={microphoneErrorToast}
								/>
							) : (
								<p className="text-sm text-muted-foreground">
									Live stream unavailable.
								</p>
							)}
						</LiveStreamBox>
					);
					const networkPanel = (className: string) => (
						<LiveNetworkPanel
							tunnelApiUrl={tunnelApiUrl}
							token={instanceToken}
							platform={platform}
							instanceId={instanceId}
							className={className}
						/>
					);
					return (
						<SessionColumns
							left={capturePanels}
							center={streamView}
							right={networkPanel}
						/>
					);
				})()}

			{/* Persisted playback: the same three columns as the live
					    view, one section per recording (or a single section
					    when nothing was recorded). Missing kinds say so in
					    their column instead of changing the layout. */}
			{showPlayback &&
				playbackSections.map((section) => (
					<PlaybackSection
						key={section.key}
						recording={section.recording}
						appLog={section.appLog}
						events={section.events}
						networkCaptures={networkLogCaptures}
					/>
				))}

			{/* Captures that no playback section shows still need a
					    home for their metadata and download. */}
			{leftoverCaptures.length > 0 && (
				<div className="flex flex-col overflow-hidden rounded-lg border bg-background">
					<div className="border-b px-3 py-2">
						<h2 className="text-sm font-semibold">Captures</h2>
					</div>
					{leftoverCaptures.map((artifact) => (
						<CaptureMetaRow key={artifact.id} artifact={artifact} />
					))}
				</div>
			)}

			{artifactsError && (
				<p className="text-sm text-red-600">
					Failed to load session artifacts: {artifactsError.message}
				</p>
			)}
			{artifactsLoading && (
				<p className="text-sm text-muted-foreground">Loading...</p>
			)}
			{nothingPersisted && instanceReady && (
				<p className="text-sm text-muted-foreground">
					No persisted artifacts yet. Captures started with persistence
					appear here after they stop.
				</p>
			)}
			<div className="pb-4" />
		</AppShell>
	);
}
